import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api.js';
import { t, wanneer, geleden, getal } from '../i18n.js';
import { St } from '../components/Status.jsx';

/**
 * Beheer › Klanten › Sensoren (docs/besluiten/sensor-koppelen.md), voor wie rechten beheert: een sensor op locatie bij een klant, met een koppelcode voor de
 * installatie. **De code is één keer te zien** en 24 uur geldig; daarna koppelt de sensor zich met een eigen certificaat.
 * Intrekken werkt meteen. De sensor op ZRO011 meldt zich met een token en staat er ter informatie bij.
 */
const GB = 1024 ** 3;
const grootte = (b) => (b >= GB ? `${getal(Math.round((b / GB) * 10) / 10)} GB` : `${getal(Math.round(b / 1024 ** 2))} MB`);
const STIL_MS = 5 * 60_000;

function Stand({ s }) {
  if (s.soort === 'lokaal') return <St soort="neutral">{t('op ZRO011, met token')}</St>;
  if (!s.certificate) return s.codeUntil ? <St soort="warning">{t('wacht op koppelen')}</St>
    : <St soort="neutral">{s.revokedAt ? t('ingetrokken') : t('niet gekoppeld')}</St>;
  if (!s.lastSeenAt) return <St soort="warning">{t('gekoppeld, nog niets ontvangen')}</St>;
  if (Date.now() - new Date(s.lastSeenAt).getTime() > STIL_MS) return <St soort="critical">{t('stil')}</St>;
  if (s.lag && s.lag.seconds >= 60 && Date.now() - new Date(s.lag.at).getTime() < 2 * 60_000) {
    return <St soort="serious">{t('loopt {n} min achter', { n: getal(Math.round(s.lag.seconds / 60)) })}</St>;
  }
  return <St soort="good">{t('realtime')}</St>;
}

export default function BeheerSensoren({ klanten, alleenKlant = null }) {
  const client = useQueryClient();
  const q = useQuery({ queryKey: ['sensoren'], queryFn: api.sensoren, refetchInterval: 30_000 });
  const [v, zetV] = useState({ tenantId: String(alleenKlant ?? klanten[0]?.id ?? ''), name: '' });
  const [code, zetCode] = useState(null);
  const [fout, zetFout] = useState(null);
  const [velden, zetVelden] = useState({});
  const [gekopieerd, zetGekopieerd] = useState(false);
  const ververs = () => client.invalidateQueries({ queryKey: ['sensoren'] });
  const zet = (k) => (e) => zetV((x) => ({ ...x, [k]: e.target.value }));
  const veld = (k, label, invoer, hulp) => (
    <label className={`field ${velden[k] ? 'bad' : ''}`}>
      <span>{label}</span>{invoer}
      {velden[k] ? <span className="err">{t(velden[k])}</span> : hulp ? <small>{hulp}</small> : null}
    </label>
  );

  const maak = async (e) => {
    e.preventDefault(); zetFout(null); zetVelden({}); zetGekopieerd(false);
    try {
      const { data } = await api.maakSensor({ tenantId: Number(v.tenantId), name: v.name.trim() });
      zetCode({ ...data, name: data.sensor.name }); zetV((x) => ({ ...x, name: '' }));
      await ververs();
    } catch (err) { zetFout(t(err.message)); zetVelden(err.details ?? {}); }
  };
  const nieuweCode = async (s) => {
    zetFout(null); zetGekopieerd(false);
    try { const { data } = await api.sensorCode(s.id); zetCode({ ...data, name: s.name }); await ververs(); } catch (err) { zetFout(t(err.message)); }
  };
  const [hernoem, zetHernoem] = useState(null);
  const bewaarNaam = async (e) => {
    e.preventDefault(); zetFout(null); zetVelden({});
    try { await api.hernoemSensor(hernoem.id, hernoem.name.trim()); zetHernoem(null); await ververs(); }
    catch (err) { zetFout(t(err.message)); zetVelden(err.details ?? {}); }
  };
  const trekIn = async (s) => {
    zetFout(null);
    try { await api.trekSensorIn(s.id); await ververs(); } catch (err) { zetFout(t(err.message)); }
  };

  const d = q.data?.data;
  /* Onder Beheer › Klant alleen de sensoren van die klant; onder Sensoren alle. */
  const zichtbaar = d ? (alleenKlant ? d.sensors.filter((s) => String(s.tenant.id) === String(alleenKlant)) : d.sensors) : [];
  return (
    <>
      {fout && <div className="melder rood" style={{ marginBottom: 14 }}>{fout}</div>}
      {d && !d.open && <div className="callout warn" style={{ marginBottom: 14 }}>{t('De ingang voor sensoren staat nog uit (SENSOR_PORT). Een sensor kan nog niet koppelen.')}</div>}
      {code && (
        <div className="callout ok" style={{ marginBottom: 14 }}>
          <b>{t('De koppelcode voor {naam}, één keer te zien:', { naam: code.name })}</b>
          <div className="rij" style={{ marginTop: 8 }}>
            <input className="input mono" readOnly value={code.command} style={{ flex: 1, minWidth: 0 }} onFocus={(e) => e.target.select()} aria-label={t('Installatieopdracht')} />
            <button type="button" className="btn" onClick={() => navigator.clipboard?.writeText(code.command).then(() => zetGekopieerd(true))}>{gekopieerd ? t('Gekopieerd') : t('Kopiëren')}</button>
          </div>
          <p className="noot">{t('Geldig tot {tot}. Draai dit op de Ubuntu-server van de klant; de sensor maakt zelf een sleutel en koppelt met {server} op poort 443, alleen uitgaand.',
            { tot: wanneer(code.expiresAt), server: code.server })}</p>
        </div>
      )}
      <section className="card">
        <header><h2>{t('Sensoren')}</h2></header>
        {q.isPending ? <p className="muted">{t('Laden…')}</p> : q.isError ? <div className="melder rood">{t(q.error.message)}</div> : zichtbaar.length ? (
          <table>
            <thead><tr><th>{t('Naam')}</th><th>{t('Stand')}</th><th className="hide-sm">{t('Buffer')}</th><th>{t('Laatst gezien')}</th><th /></tr></thead>
            <tbody>{zichtbaar.map((s) => (
              <tr key={s.id}>
                <td>{hernoem?.id === s.id ? (
                  <form className="rij" style={{ gap: 6 }} onSubmit={bewaarNaam}>
                    <input className="input" value={hernoem.name} autoFocus aria-label={t('Naam')} style={{ minWidth: 0 }}
                      onChange={(e) => zetHernoem((x) => ({ ...x, name: e.target.value }))} onKeyDown={(e) => { if (e.key === 'Escape') zetHernoem(null); }} />
                    <button className="btn primary" disabled={hernoem.name.trim().length < 2}>{t('Opslaan')}</button>
                    <button type="button" className="btn" onClick={() => zetHernoem(null)}>{t('Annuleren')}</button>
                  </form>
                ) : <><b>{s.name}</b> <button type="button" className="namelink" style={{ fontSize: 12 }} onClick={() => { zetVelden({}); zetHernoem({ id: s.id, name: s.name }); }}>{t('Naam wijzigen')}</button></>}
                  {hernoem?.id === s.id && velden.name && <div className="err">{t(velden.name)}</div>}
                  <br /><span className="muted" style={{ fontSize: 12 }}>{s.tenant.name}
                  {` · ${s.address ? t('adres {adres}', { adres: s.address }) : t('adres nog niet gemeld')}`}{` · ${s.sources === 1 ? t('1 bron') : t('{n} bronnen', { n: s.sources })}`}{s.version && ` · ${t('versie {v}', { v: s.version })}`}</span></td>
                <td><Stand s={s} />
                  {s.certificate && <div className="muted" style={{ fontSize: 12 }}>{t('certificaat tot {tot}', { tot: wanneer(s.certificate.notAfter) })}</div>}
                  {!s.certificate && s.codeUntil && <div className="muted" style={{ fontSize: 12 }}>{t('code geldig tot {tot}', { tot: wanneer(s.codeUntil) })}</div>}</td>
                <td className="hide-sm" style={{ fontSize: 13 }}>{s.buffer ? <>{t('{n} van {max}', { n: grootte(s.buffer.bytes), max: grootte(s.buffer.maxBytes) })}
                  {s.diskFreeBytes !== null && <div className="muted">{t('{n} vrij op schijf', { n: grootte(s.diskFreeBytes) })}</div>}</> : <span className="muted">—</span>}</td>
                <td className="muted">{s.lastSeenAt ? geleden(s.lastSeenAt) : t('nog niet')}
                  {s.lastHeartbeatAt && <div style={{ fontSize: 12 }}>{t('heartbeat {wanneer}', { wanneer: geleden(s.lastHeartbeatAt) })}</div>}</td>
                <td style={{ whiteSpace: 'nowrap' }}>{s.soort === 'locatie' && s.active && <>
                  <button type="button" className="namelink" onClick={() => nieuweCode(s)}>{t('Nieuwe koppelcode')}</button>
                  {(s.certificate || s.codeUntil) && <> · <button type="button" className="namelink gevaar" onClick={() => trekIn(s)}>{t('Intrekken')}</button></>}
                </>}</td>
              </tr>
            ))}</tbody>
          </table>
        ) : <p className="empty">{t('Nog geen sensoren.')}</p>}
        <p className="noot">{t('Een sensor stuurt elke regel binnen een seconde door, en buffert alleen als ADTS ZERO of de lijn er niet is. Stil na 5 minuten is een kritiek alert; een buffer die volloopt een hoog alert.')}</p>
      </section>
      <section className="card" style={{ marginTop: 18 }}>
        <header><h2>{t('Sensor toevoegen')}</h2></header>
        <form className="form" onSubmit={maak} noValidate>
          {/* Op een klant-tab (alleenKlant) ligt de klant vast; alleen op het losse Sensoren-scherm kies je hem. */}
          {alleenKlant
            ? veld('tenantId', t('Klant'), <input className="input" value={klanten.find((k) => String(k.id) === String(alleenKlant))?.name ?? ''} disabled />)
            : veld('tenantId', t('Klant'), <select value={v.tenantId} onChange={zet('tenantId')}>{klanten.map((k) => <option key={k.id} value={k.id}>{k.name}</option>)}</select>)}
          {veld('name', t('Naam'), <input className="input" value={v.name} onChange={zet('name')} placeholder="NOORDKADE-AMS-ZS01" />)}
          <div><button className="btn primary" disabled={v.name.trim().length < 2 || !v.tenantId}>{t('Sensor en koppelcode maken')}</button></div>
          <p className="noot">{t('De klant zet een Ubuntu 24.04-server neer (2 vCPU, 4 GB, 100 GB) en draait het installatiebestand met de koppelcode. Het adres meldt de sensor zelf; verandert het (DHCP), dan geeft ADTS ZERO een alert. Daarna kies je de sensor bij de bronnen onder Ontvangen via.')}</p>
        </form>
      </section>
    </>
  );
}
